import "@diligentcorp/atlas-web-component-icons/lib/avatar/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/user-management/index.js";
import "@diligentcorp/atlas-web-component-icons/lib/sign-out/index.js";
import { css, html, LitElement } from "lit";
import { classMap } from "lit/directives/class-map.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { TOGGLE_MODAL_EVENT_NAME } from "../configuration/constants.js";
import { dispatchToggleModalEvent } from "../configuration/utils.js";
import { buttonDefaults, componentVar, iconButton, mediumButton, semanticVar, tertiaryButton, } from "./styles.js";
const menuVar = (name) => componentVar(`menu-${name}`);
const menuItemVar = (name) => menuVar(`item-${name}`);
export class Menu extends LitElement {
    constructor() {
        super();
        this.dialogRef = createRef();
        this.toggleMenu = (e) => {
            this.open = !this.open;
            if (this.open) {
                dispatchToggleModalEvent(this, true, "menu", () => {
                    this.open = false;
                    this.dialogRef.value?.close();
                });
                this.addDocumentEventHandler();
            }
            else {
                dispatchToggleModalEvent(this, false, "menu");
                this.removeDocumentEventHandler();
            }
            e.stopPropagation();
        };
        this.handleDocumentClick = (event) => {
            if (this.dialogRef.value?.open && !this.dialogRef.value?.contains(event.target)) {
                this.open = false;
                this.dialogRef.value?.close();
                this.removeDocumentEventHandler();
            }
        };
        this.handleOpenMenu = (event) => {
            if (event.target !== this && this.open) {
                this.open = false;
                this.dialogRef.value?.close();
            }
        };
        this.open = false;
    }
    render() {
        const menu = this.renderMenu();
        const hasMenu = menu !== undefined;
        return html `
      <button
        class=${classMap({
            tertiary: true,
            medium: true,
            "icon-button": true,
            ...this.menuButtonClasses,
        })}
        @click=${hasMenu ? this.toggleMenu : undefined}
        aria-haspopup=${hasMenu ? "dialog" : "false"}
        aria-expanded=${this.open ? "true" : "false"}
        aria-controls=${hasMenu ? this.menuId : ""}
        aria-label=${this.menuButtonArialLabel}
        title=${this.menuButtonArialLabel}
      >
        ${this.renderButtonContent()}
      </button>
      ${when(hasMenu, () => html `
          <dialog
            ${ref(this.dialogRef)}
            id=${this.menuId}
            aria-label=${this.menuAriaLabel}
            aria-hidden=${this.open ? "false" : "true"}
            ?open=${this.open}
            @click=${this.handleDialogClick}
            @close=${this.handleDialogClose}
          >
            ${this.renderMenu()}
          </dialog>
        `)}
    `;
    }
    get menuButtonClasses() {
        return undefined;
    }
    renderMenuItem(item) {
        if (!item.url) {
            return;
        }
        const title = typeof item.label === "object" ? item.label.strings[0] : item.label;
        return html `
      <li role="menuitem" aria-label=${title}>
        <a
          href=${item.url}
          class=${classMap({ "menu-item": true, destructive: item.destructive === true })}
        >
          <span class="icon" aria-hidden="true">${item.icon}</span>
          <span>${item.label}</span>
        </a>
      </li>
    `;
    }
    renderDivider() {
        return html `<hr />`;
    }
    addDocumentEventHandler() {
        document.addEventListener(TOGGLE_MODAL_EVENT_NAME, this.handleOpenMenu);
        document.addEventListener("click", this.handleDocumentClick);
    }
    removeDocumentEventHandler() {
        document.removeEventListener(TOGGLE_MODAL_EVENT_NAME, this.handleOpenMenu);
        document.removeEventListener("click", this.handleDocumentClick);
    }
    handleDialogClick(e) {
        e.stopPropagation();
    }
    handleDialogClose() {
        if (this.open) {
            this.open = false;
        }
        this.removeDocumentEventHandler();
    }
}
Menu.properties = {
    open: { type: Boolean, reflect: true },
};
Menu.styles = [
    buttonDefaults,
    tertiaryButton,
    mediumButton,
    iconButton,
    css `
      :host {
        display: flex;
        position: relative;
      }
      :host([open]) > .icon-button {
        background-color: var(--button-active-background);
      }

      dialog {
        position: absolute;
        top: 100%;
        z-index: calc(Infinity);
        margin-right: 0;
        border: none;
        border-radius: ${menuVar("border-radius")};
        background-color: ${menuVar("background-color")};
        box-shadow: ${menuVar("box-shadow")};
        padding: 0;
        text-align: left;
        overflow: hidden;
        min-width: 220px;
        transform: translateY(var(--lens-core-spacing-0-5));
        animation: slide-down var(--atlas-global-nav-animation-duration)
          cubic-bezier(0.33, 1, 0.68, 1);
      }
      dialog:focus {
        outline-style: solid;
        outline-width: var(--lens-semantic-border-width-focus-width);
        outline-color: var(--lens-semantic-color-ui-focus-ring);
        outline-offset: var(--lens-semantic-border-width-focus-offset);
      }

      ul {
        display: flex;
        flex-direction: column;
        padding: 0;
        margin: 0;
      }
      ul li {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        font-size: ${menuItemVar("font-size")};
        font-weight: ${menuItemVar("font-weight")};
        line-height: ${menuItemVar("line-height")};
      }
      .menu-item {
        display: inline-flex;
        box-sizing: border-box;
        align-items: center;
        justify-content: flex-start;
        gap: ${menuItemVar("gap")};
        padding-left: ${menuItemVar("padding-left")};
        padding-right: ${menuItemVar("padding-right")};
        padding-top: ${menuItemVar("padding-top")};
        padding-bottom: ${menuItemVar("padding-bottom")};
        height: ${menuItemVar("height")};
        font: inherit;
        text-decoration: none;
        border: none;
        background-color: ${menuItemVar("default-background-color")};
        color: ${menuItemVar("default-color")};

        &.destructive {
          color: ${menuItemVar("destructive-default-color")};
        }
      }
      .menu-item.destructive {
        color: ${menuItemVar("destructive-default-color")};
      }

      .menu-item .icon {
        display: inline-flex;
      }

      .menu-item:not(:disabled):hover {
        cursor: pointer;
        background-color: ${menuItemVar("hover-background-color")};
        color: ${menuItemVar("hover-color")};
      }
      .menu-item.destructive:not(:disabled):hover {
        background-color: ${menuItemVar("destructive-hover-background-color")};
        color: ${menuItemVar("destructive-hover-color")};
      }
      .menu-item:not(:disabled):active {
        background-color: ${menuItemVar("active-background-color")};
        color: ${menuItemVar("active-color")};
      }
      .menu-item:not(:disabled):focus {
        outline-color: var(--lens-semantic-color-ui-focus-ring);
        outline-style: solid;
        outline-offset: -2px;
      }

      ul:first-child li:first-of-type .menu-item {
        border-top-left-radius: ${menuVar("border-radius")};
        border-top-right-radius: ${menuVar("border-radius")};
      }

      ul:last-child li:last-of-type .menu-item {
        border-bottom-left-radius: ${menuVar("border-radius")};
        border-bottom-right-radius: ${menuVar("border-radius")};
      }

      hr {
        width: 100%;
        margin: 0;
        border-top-width: 0;
        border-bottom-width: ${semanticVar("border-width-thin")};
        border-bottom-color: ${semanticVar("color-ui-divider-default")};
        border-bottom-style: solid;
      }

      @keyframes slide-down {
        from {
          opacity: 0;
          transform: translateY(calc(-1 * var(--lens-core-spacing-0-5)));
        }
        to {
          opacity: 1;
          transform: translateY(var(--lens-core-spacing-0-5));
        }
      }
    `,
];
